import { type EntityType } from 'core/domain/EntityType'

import { type BulkRate } from '../domain/BulkRate'
import { type BulkRateAvailability } from '../domain/BulkRateAvailability'
import { type BulkSpotQuote } from '../domain/BulkSpotQuote'
import { type QuoteMode } from '../domain/QuoteMode'
import { type Shipper } from '../domain/Shipper'
import { type SpotQuoteStatus } from '../domain/SpotQuoteStatus'
import { mapApiToQuote, type RawQuote } from './SpotQuoteMapper'

type SubmitSpotQuote = {
  id: string
  flatRate: number
  notes?: string
}

type RawSubmitSpotQuote = {
  rate_uuid: string
  flat_rate: number
  carrier_notes: string
}

type RawShipper = {
  company_name: string | null
  email: string
  id: number
  logo_email_url: string | null
  phone_number: string | null
}

type RawBulkRateAvailability = {
  is_bid_available: boolean
  reason: string | null
}

type RawBulkRate = {
  availability: RawBulkRateAvailability
  carrier_notes: string | null
  flat_rate: string | null
  per_mile_rate: number | null
  quote: RawQuote
  status: SpotQuoteStatus
  uuid: string
}

export type RawBulkCarrier = {
  name: string
  email: string
  entity_type?: EntityType
}

export type RawBulkSpotQuote = {
  all_quotes_expired: boolean
  all_rates_sent: boolean
  carrier?: RawBulkCarrier
  carrier_name: string
  carrier_email: string
  mode: QuoteMode
  number_shipments: number
  rates: Array<RawBulkRate>
  shipper: RawShipper
  soonest_bid_deadline: string
}

function mapApiToBulkRateAvailability(
  rawBulkRateAvailability: RawBulkRateAvailability
): BulkRateAvailability {
  return {
    isBidAvailable: rawBulkRateAvailability.is_bid_available,
    reason: rawBulkRateAvailability.reason || undefined,
  }
}

function mapApiToBulkRate(rawBulkRate: RawBulkRate): BulkRate {
  return {
    availability: mapApiToBulkRateAvailability(rawBulkRate.availability),
    carrierNotes: rawBulkRate.carrier_notes || undefined,
    flatRate: rawBulkRate.flat_rate || undefined,
    perMileRate: rawBulkRate.per_mile_rate ?? undefined,
    quote: mapApiToQuote(rawBulkRate.quote),
    status: rawBulkRate.status,
    uuid: rawBulkRate.uuid,
  }
}

function mapApiToShipperInformation(rawShipper: RawShipper): Shipper {
  return {
    email: rawShipper.email,
    id: rawShipper.id,
    logoUrl: rawShipper.logo_email_url || undefined,
    name: rawShipper.company_name || undefined,
    phoneNumber: rawShipper.phone_number || undefined,
  }
}

function mapApiToBulkCarrier(carrier?: RawBulkCarrier) {
  if (!carrier) return undefined

  return {
    name: carrier?.name,
    email: carrier?.email,
    entityType: carrier?.entity_type,
  }
}

export function mapApiToBulkSpotQuote(rawBulkSpotQuote: RawBulkSpotQuote): BulkSpotQuote {
  return {
    allQuotesExpired: rawBulkSpotQuote.all_quotes_expired,
    allRatesSent: rawBulkSpotQuote.all_rates_sent,
    carrier: mapApiToBulkCarrier(rawBulkSpotQuote.carrier),
    carrierName: rawBulkSpotQuote.carrier_name,
    carrierEmail: rawBulkSpotQuote.carrier_email,
    mode: rawBulkSpotQuote.mode,
    numberShipments: rawBulkSpotQuote.number_shipments,
    rates: rawBulkSpotQuote.rates.map(mapApiToBulkRate),
    shipper: mapApiToShipperInformation(rawBulkSpotQuote.shipper),
    soonestBidDeadline: new Date(rawBulkSpotQuote.soonest_bid_deadline),
  }
}

export function mapBulkSpotQuoteToApi(rate: SubmitSpotQuote): RawSubmitSpotQuote {
  return {
    rate_uuid: rate.id,
    flat_rate: rate.flatRate,
    carrier_notes: rate.notes ?? '',
  }
}
